import {
  SnackbarApiErrorMessage,
  useDataProvider,
  useEntityPermission,
  useLocalization,
  useSnackbar,
} from '@ui-framework/index';
import ListBoxSelectorWithSearch from '@product-framework/components/ListBoxSelectorWithSearch';
import UnPaginatedList from '@product-framework/components/UnPaginatedList';
import { useServiceProviderScope } from '@product-framework/pages/ServiceProvider/scope/ServiceProviderScopeContext';

function BrandsSelector({
  onChange = () => undefined,
  selectedIndustry = {},
  permissions,
}) {
  const leftNameKey = 'brandName';
  const leftValueKey = 'id';
  const rightValueKey = 'id';
  const limit = 10;

  const { formatMessage } = useLocalization();
  const rightTitle = formatMessage({
    id: 'EXCLUDED_FROM_SERVICE',
    defaultMessage: 'Excluded from service',
  });

  const provider =
    process.env.NODE_ENV === 'development' ? 'sxclServices' : 'default';
  const { get, post, remove } = useDataProvider(provider);
  const snackbar = useSnackbar();
  const { skillsConfig } = useServiceProviderScope();
  const config = skillsConfig ?? {};
  const brandMessages = config?.ui?.messages?.brandsSelector;

  const { isReadOnly } = useEntityPermission();
  const readOnly = isReadOnly(permissions?.brandId, '');

  const getLeftItems = async (page, searchParams) => {
    // If no industry is selected, return empty array
    if (!selectedIndustry?.value) {
      return [];
    }
    let searchFilter = '';
    if (searchParams) {
      searchFilter = `&brandName=$like:${searchParams}`;
    }
    const response = await get(
      `${config?.api?.brandsSelector?.listBoxLeft}?industry.id=$eq:${
        selectedIndustry.value
      }&isActive=$eq:true&page=${page}&limit=${limit}${searchFilter}`,
    );

    if (response.ok) {
      const { data = [], ...other } = response.data;
      const resultsData =
        data?.map(item => ({
          label: item[leftNameKey],
          value: item[leftValueKey],
        })) || [];
      return { data: resultsData, ...other };
    }
    snackbar.showMessage({
      type: 'error',
      data: <SnackbarApiErrorMessage errors={response?.errors} />,
    });
    return [];
  };

  const getRightItems = async page => {
    // If no industry is selected, return empty array
    if (!selectedIndustry?.value) {
      return [];
    }
    const response = await get(
      `${config?.api?.brandsSelector?.listBoxRight}?industryId=${selectedIndustry.value}&page=${page}&limit=${limit}`,
    );
    if (response.ok) {
      const { data = [], ...other } = response.data;
      const resultsData =
        data?.map(item => ({
          label: item[leftNameKey],
          value: item,
          status: item.status,
        })) || [];
      onChange(resultsData);
      return { data: resultsData, ...other };
    }
    snackbar.showMessage({
      type: 'error',
      data: <SnackbarApiErrorMessage errors={response?.errors} />,
    });
    return [];
  };

  const onItemsAdded = async items => {
    let successItems = 0;
    if (Array.isArray(items)) {
      // eslint-disable-next-line no-restricted-syntax
      for (const item of items) {
        // eslint-disable-next-line no-await-in-loop
        const response = await post(
          `${config?.api?.brandsSelector?.listBoxAdd}`,
          {
            brandId: item,
            industryId: selectedIndustry?.value,
          },
        );
        if (!response.ok) {
          snackbar.showMessage({
            type: 'error',
            data: <SnackbarApiErrorMessage errors={response?.errors} />,
          });
        } else {
          successItems += 1;
        }
      }
      if (successItems === items.length) {
        snackbar.showMessage(formatMessage(brandMessages?.saveSuccess));
      }
    }
  };

  const onItemsRemoved = async items => {
    let successItems = 0;
    if (Array.isArray(items)) {
      // eslint-disable-next-line no-restricted-syntax
      for (const item of items) {
        // eslint-disable-next-line no-await-in-loop
        const response = await remove(
          `${config?.api?.brandsSelector?.listBoxRemove}/${item[rightValueKey]}`,
        );
        if (!response.ok) {
          snackbar.showMessage({
            type: 'error',
            data: <SnackbarApiErrorMessage errors={response?.errors} />,
          });
        } else {
          successItems += 1;
        }
      }
      if (successItems === items.length) {
        snackbar.showMessage(formatMessage(brandMessages?.removeSuccess));
      }
    }
  };

  if (readOnly) {
    return <UnPaginatedList title={rightTitle} getItems={getRightItems} />;
  }

  return (
    <ListBoxSelectorWithSearch
      height="465px"
      getLeftItems={getLeftItems}
      getRightItems={getRightItems}
      parentId={selectedIndustry?.value || null}
      onChange={onChange}
      onItemsAdded={onItemsAdded}
      onItemsRemoved={onItemsRemoved}
      leftTitle={`${selectedIndustry?.label} Brands` || ''}
      rightTitle={rightTitle}
      searchPlaceholder={formatMessage({
        id: 'SEARCH_BRANDS_PLACEHOLDER',
        defaultMessage: 'Search Brands',
      })}
    />
  );
}

export default BrandsSelector;
